import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDownIcon, DownloadIcon, FileTextIcon, HistoryIcon, ImageIcon, MapPinIcon, PaperclipIcon, PlusIcon, SparklesIcon } from 'lucide-react';
import type { CitizenRequest, RequestAttachment } from '../../data/demandes';
const attachmentIcons: Record<RequestAttachment['type'], typeof ImageIcon> = {
  image: ImageIcon,
  pdf: FileTextIcon,
  form: FileTextIcon
};
export function RequestDetailsPanel({
  request
}: {
  request: CitizenRequest;
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  return <div className="space-y-5" data-id="element-1012" data-mp="2jhuo6zawatu9">
      <section aria-labelledby="description-titre" className="rounded-3xl bg-ville-sand p-6 sm:p-7" data-id="element-1013" data-mp="2jjnugdhljc1q">
        <h2 id="description-titre" className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-1014" data-mp="2ogxh5ldlp9lv">
          Description de la requête
        </h2>
        <p className="mt-3 text-[16px] leading-relaxed text-slate-700" data-id="element-1015" data-mp="u36a8tmnp3u4">{request.description}</p>
        <p className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-slate-500" data-id="element-1016" data-mp="2k2ar1xz63v5q">
          <SparklesIcon className="h-3.5 w-3.5 text-ville-indigo" aria-hidden="true" data-id="element-1017" data-mp="2fblx8c00mm3f" />
          Demande créée par {request.createdBy}
        </p>
      </section>

      <div className="grid gap-5 sm:grid-cols-2" data-id="element-1018" data-mp="2baiftl2bki03">
        <section aria-labelledby="emplacement-titre" className="rounded-3xl border border-slate-200 p-6" data-id="element-1019" data-mp="26b5td1fd1cy1">
          <h2 id="emplacement-titre" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-1020" data-mp="331om6j8mvbxt">
            Emplacement
          </h2>
          <p className="mt-3 flex items-start gap-2 text-[15px] font-bold leading-snug text-ville-navy" data-id="element-1021" data-mp="2z0cwkdpreqpm">
            <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1022" data-mp="2u2l2bd1n6si9" />
            {request.address}
          </p>
          <p className="mt-1 pl-6 text-sm text-slate-500" data-id="element-1023" data-mp="1ilutrn9qxolh">{request.fullAddress}</p>
        </section>

        <section aria-labelledby="dates-titre" className="rounded-3xl border border-slate-200 p-6" data-id="element-1024" data-mp="1dlp4gdqy8e6k">
          <h2 id="dates-titre" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-1025" data-mp="1f35qhn3sk3up">
            Dates
          </h2>
          <dl className="mt-3 space-y-2 text-[15px]" data-id="element-1026" data-mp="1k1pedt1y3ppp">
            <div className="flex items-baseline justify-between gap-4" data-id="element-1027" data-mp="1638x586gigtb">
              <dt className="text-slate-500" data-id="element-1028" data-mp="1b1sj38l1vslo">Créée le</dt>
              <dd className="font-bold tabular-nums text-ville-navy" data-id="element-1029" data-mp="1b092fgvhffjd">{request.createdOn}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4" data-id="element-1030" data-mp="wbladkkuhny6">
              <dt className="text-slate-500" data-id="element-1031" data-mp="114o53w1cz3fy">Résolue</dt>
              <dd className={`font-bold tabular-nums ${request.resolvedOn ? 'text-ville-navy' : 'text-slate-300'}`} data-id="element-1032" data-mp="nc80iqst6xcx">
                {request.resolvedOn ?? '-- / -- / --'}
              </dd>
            </div>
          </dl>
        </section>
      </div>

      <section aria-labelledby="fichiers-titre" className="rounded-3xl border border-slate-200 p-6 sm:p-7" data-id="element-1033" data-mp="dg4wffeeconc">
        <h2 id="fichiers-titre" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-1034" data-mp="iezegf4ji96e">
          Fichiers joints
        </h2>

        {request.attachments.length === 0 ? <p className="mt-3 text-[15px] text-slate-500" data-id="element-1035" data-mp="3q00m0rqn39bc">Aucun fichier joint.</p> : <ul className="mt-4 space-y-2" data-id="element-1036" data-mp="8ng4dm2wfssu">
            {request.attachments.map(file => {
          const Icon = attachmentIcons[file.type];
          return <li key={file.name} className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3" data-id="element-1037" data-mp="homkhu1mz1lk">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-1038" data-mp="rga81jv2nyhr">
                    <Icon className="h-5 w-5" data-id="element-1039" data-mp="10fx6pxwf2q74" />
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-1040" data-mp="wfdd34b1y4pq">
                    <span className="block truncate text-[15px] font-bold text-ville-navy" data-id="element-1041" data-mp="wgoit9o25mjh">{file.name}</span>
                    <span className="block text-xs text-slate-500" data-id="element-1042" data-mp="2pxkf2hw99zek">{file.size}</span>
                  </span>
                  <button type="button" aria-label={`Télécharger ${file.name}`} className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1043" data-mp="2uuu9yd2ez39s">
                    <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1044" data-mp="2c05ld0yyz5gs" />
                  </button>
                </li>;
        })}
          </ul>}

        <button type="button" className="mt-4 inline-flex items-center gap-2 rounded-2xl border border-dashed border-slate-300 px-4 py-3 text-sm font-bold text-slate-500 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1045" data-mp="2kziz45u4mg7b">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1046" data-mp="1gq90ydl9410y" />
          Ajouter un fichier
          <PaperclipIcon className="h-4 w-4 opacity-50" aria-hidden="true" data-id="element-1047" data-mp="1bx7jpyxknpe7" />
        </button>
      </section>

      <section aria-labelledby="historique-titre" className="rounded-3xl border border-slate-200 px-6 py-4 sm:px-7" data-id="element-1048" data-mp="16yd393n2ucx9">
        <h2 id="historique-titre" className="sr-only" data-id="element-1049" data-mp="121cuum2r8y2z">
          Historique de la requête
        </h2>
        <button type="button" onClick={() => setHistoryOpen(open => !open)} aria-expanded={historyOpen} className="flex w-full items-center justify-between gap-4 py-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1050" data-mp="wzni91zvwccs">
          <span className="inline-flex items-center gap-2 rounded-full bg-ville-mist px-3 py-1.5 text-sm font-bold text-ville-deep" data-id="element-1051" data-mp="2nbbmnum50nve">
            <HistoryIcon className="h-4 w-4" aria-hidden="true" data-id="element-1052" data-mp="31j6hrcfqehbl" />
            Historique
          </span>
          <span className="flex items-center gap-2 text-sm text-slate-500" data-id="element-1053" data-mp="36a6fgjwod3f5">
            {request.timeline.length} étapes
            <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ease-ville ${historyOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1054" data-mp="2sjb0qqzhvs3n" />
          </span>
        </button>

        {historyOpen && <motion.ol initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-4 space-y-4 border-t border-slate-100 pt-4" data-id="element-1055" data-mp="3l52qt5zs66wg">
            {request.timeline.map((step, index) => <li key={step.label} className="flex gap-4" data-id="element-1056" data-mp="1ztub2139epb4" data-mpi={index}>
                <div className="flex flex-col items-center" data-id="element-1057" data-mp="1zs9iui4f3igf">
                  <span className={`h-3 w-3 rounded-full ${index === request.timeline.length - 1 ? 'bg-ville-indigo' : 'bg-slate-300'}`} aria-hidden="true" data-id="element-1058" data-mp="24sxgvnr2czk5" />
                  {index < request.timeline.length - 1 && <span className="mt-1 w-px flex-1 bg-slate-200" aria-hidden="true" data-id="element-1059" data-mp="269b13u51p2dl" />}
                </div>
                <div className="pb-1" data-id="element-1060" data-mp="218x3l612latt">
                  <p className="text-sm font-bold text-ville-navy" data-id="element-1061" data-mp="1dkdvorlxn029">{step.label}</p>
                  <p className="text-sm text-slate-500" data-id="element-1062" data-mp="19jtyjo1czwg4">{step.date}</p>
                </div>
              </li>)}
          </motion.ol>}
      </section>
    </div>;
}